import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../../../api/client';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { ICM_LIST_TAKE, icmTruncationNote } from '../icmPaging';
import { useNotify } from '../../../components/notifications';
import { useConfirm } from '../../../components/confirm';
import { useNavActions } from '../../../hooks/useNavActions';
import { fmtLocalDate, fmtLocalDateTime, nowClientWire } from '../../../utils/datetime';
import { useAuthStore } from '../../../auth/store';
import '../../backlog/backlog.css';
import '../../modules/SettingsDetailPage.css';
import type { RequisitionRow } from './RequisitionListPage';
import { DateFilterSelect, defaultDateFilter, monthsOf,
         type DateFilterId } from './IcmDateFilter';

/* The Date Filter is the SHARED picker (standing rule 8). This page carried its
   own copy of the type + list + markup -- the duplication IcmDateFilter was
   created to remove, and the reason its default had drifted away from the other
   six screens. */

export default function RequisitionReviewListPage() {
  const notify = useNotify();
  const { confirm, prompt } = useConfirm();
  // MAC rights (hide, never grey): Review / Cancel / RollBack / inline edit change data = Write.
  const perm = useNavActions('icm-requisition-review');

  const [rows, setRows] = useState<RequisitionRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [acting, setActing] = useState(false);

  /* DateReviewed / ReviewedBy PREVIEW (legacy Grid_SelectionChanged,
     RequisitionViewcontroller.cs :1411-1436). Ticking a row on the Review view
     stamps `obj.ReviewedDate = DateTime.Now` and `obj.ReviewedBy = <current
     employee>` on the in-memory object, and UNticking it sets both back to
     null — so the two columns fill in as you choose rows and the reviewer can
     see what they are about to sign off. Nothing is written until the Review
     action runs; AI_ICM_Requisition_Review_SP stamps the real values.
     AI showed the columns but never previewed anything, so they read blank
     however many rows you ticked (user report 2026-09-29).
     The timestamp is captured AT TICK TIME, not at render, so it does not
     drift with re-renders — legacy takes DateTime.Now in the handler. It is the
     naive nowClientWire(), never toISOString(): the fmtLocal* helpers read the
     wall-clock literally (ADR-0008), so a UTC string would preview the UTC time. */
  const meName = useAuthStore(st => st.user?.name || st.user?.email || '');
  const [reviewStamps, setReviewStamps] = useState<Map<string, string>>(new Map());

  const handleSelectionChange = useCallback((next: Set<string | number>) => {
    setSelected(next);
    setReviewStamps(prev => {
      const out = new Map<string, string>();
      const now = nowClientWire();
      next.forEach(id => {
        const key = String(id);
        out.set(key, prev.get(key) ?? now);   // keep an existing tick's moment
      });
      return out;                             // untick drops it, as legacy nulls it
    });
  }, []);

  // Inline row-edit lookups. Legacy Requisition_ListView_Review is
  // InlineEditMode="Inline" AllowEdit="True", and the only editable cells are
  // OrderQty / DeliveryPriority / Department.
  const [priorities, setPriorities] = useState<{ id: string; name: string }[]>([]);
  const [departments, setDepartments] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const [dp, dept] = await Promise.all([
          api.get('/api/v1/delivery-priorities'),
          api.get('/api/v1/system/departments'),
        ]);
        if (cancelled) return;
        // The two endpoints do NOT share a shape: /delivery-priorities returns a
        // bare array, /system/departments the standard {success,data} envelope.
        const norm = (d: unknown) => {
          const box = d as { data?: unknown[]; items?: unknown[] } | undefined;
          const arr = Array.isArray(d) ? d : (box?.data ?? box?.items ?? []);
          return (arr as Record<string, unknown>[])
            .map(o => ({ id: String(o.oid ?? o.id ?? ''), name: String(o.name ?? '') }))
            .filter(o => o.id && o.name);
        };
        setPriorities(norm(dp.data));
        setDepartments(norm(dept.data));
      } catch { /* non-fatal: the row simply stays read-only */ }
    })();
    return () => { cancelled = true; };
  }, []);

  /** Commit one inline ROW edit (the pencil's Save). Legacy edits the record in
   *  place before review; AI_ICM_Requisition_ReviewEdit_SP applies the same
   *  three fields and recomputes ExpPrice / TotalItems server-side. */
  async function saveRow(row: RequisitionRow, values: Record<string, string>) {
    const dp = priorities.find(o => o.name === values.deliveryPriorityName);
    const dept = departments.find(o => o.name === values.departmentName);
    try {
      await api.put(`/api/v1/requisitions/${row.oid}/review-edit`, {
        orderQty: Number(values.orderQty),
        deliveryPriorityId: dp?.id ?? null,
        departmentId: dept?.id ?? null,
      });
      await load();
      notify.success('Saved successfully.');
    } catch (e) {
      const d = (e as { response?: { data?: unknown } })?.response?.data;
      notify.error(typeof d === 'string' && d ? d : 'Save failed.');
      throw e;   // keeps the row open so the user can correct it
    }
  }
  // Legacy RequisitionDateFilter — a single-choice action over the REQUESTED
  // date, defaulting to 3M (RequisitionViewcontroller ~239).
  // Legacy Requisitionview ("History") opens the requisitions already past
  // review instead of the pending-review working set.
  const [history, setHistory] = useState(false);

  /* ONE FILTER PER VIEW, not one filter reset on the toggle.
     Each view carries its own window -- a pending queue opens on ALL so nothing
     awaiting action is hidden, History opens on 3M -- and each remembers what
     the user last picked there.
     Resetting a single piece of state from an effect instead RACED: toggling to
     History committed (history=true, filter=ALL), fired a load, then the effect
     set 3M and fired a second one; the ALL response is the bigger payload, so it
     could land LAST and leave the grid showing 1,524 rows under a picker reading
     "3M". Deriving the value removes the second load entirely. */
  const [queueDateFilter, setQueueDateFilter] = useState<DateFilterId>(defaultDateFilter(false));
  const [histDateFilter,  setHistDateFilter]  = useState<DateFilterId>(defaultDateFilter(true));
  const dateFilter    = history ? histDateFilter : queueDateFilter;
  const setDateFilter = history ? setHistDateFilter : setQueueDateFilter;

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const t0 = performance.now();
    try {
      /* LEGACY TaskStatus (ICM/Requisition.cs): 0 PendingReview,
         1 PendingApproval, 2 PendingOrdering, 3 PendingOrderingApproval,
         4 PendingReceived, 5 PartiallyReceived, 6 Received, 7 Cancelled,
         8-17 Level1Pending..Level10Pending, 18 PendingSubmission. (These were
         one too high until 2026-09-24.)

         WORKING SET = pending review (0) + pending approval (1).

         HISTORY = legacy's single criterion, verbatim:
             cs.Criteria["Filter"] = "[Status] >= 'PendingOrdering'"
         (Requisitionview_Execute). That is `>= 2`, which reaches 7 Cancelled and
         the whole Level*Pending chain -- so it is sent as a MINIMUM, not as the
         enumerated list '2,3,4,5,6' used here before. The list stopped at 6 and
         hid every cancelled requisition: 1,261 rows against legacy's 1,446 on
         AlpacaLims_GoLive_Demo_20261005, 185 missing (owner report 2026-10-05). */
      /* The date window goes to the SERVER, like every other ICM list. This page
         used to filter in the BROWSER instead
             const cutoff = new Date(); cutoff.setMonth(cutoff.getMonth() - months);
             rows.filter(r => new Date(r.requestedDate) >= cutoff)
         which was wrong three ways: it never saw the SP's calendar window, so the
         2026-10-06 parity fix could not reach this screen; it read 1Y/2Y/5Y as
         12/24/60 MONTHS; and JS setMonth overflows (31 March minus one month is
         3 March). It also filtered only what had been fetched. */
      const res = await api.get('/api/v1/requisitions',
        { params: { ...(history ? { statusMin: 2 } : { status: '0,1' }),
                    /* The QUEUE takes legacy's ELAPSED window
                       (`>= AddMonths/AddYears(Now(), -N)`, RequisitionViewcontroller
                       :2974-2986) -- the only ICM list that does. Proven against
                       GoLive 2026-10-06: Review at 6M shows 13 rows, which is
                       elapsed; the calendar window every other list uses gives 14.
                       History keeps the calendar rule until a G5 reading says
                       otherwise. */
                    /* HISTORY windows on ReviewedDate (legacy :2947); the QUEUE
                       on RequestedDate with the elapsed rule (:2974-2986). Two
                       different columns AND two different rules on one page. */
                    ...(history ? { dateOnReviewed: true } : { dateElapsed: true }),
                    /* THE QUEUE ONLY. Requisition_ListView_Review is the one
                       view of the four this SP serves that re-points BOTH of
                       these columns:
                         StockQty  Item.StockQty      (raw, not DivisionStock)
                         Catalog#  Item.VendorCatName (not the requisition's)
                       Its HISTORY view (Requisition_ListView_ViewMode) does
                       neither -- gated DivisionStock and the requisition's own
                       Catalog, like the two Item Requisition grids. Sending
                       rawStockQty on both was wrong for History. */
                    ...(history ? {} : { rawStockQty: true, catalogFromItem: true }),
                    months: monthsOf(dateFilter), take: ICM_LIST_TAKE } });
      const body = res.data as { items?: RequisitionRow[]; total?: number };
      const items = body.items ?? [];
      setRows(items);
      // Same uncapped fetch as Item Requisition -- 50 rows, silently.
      const note = icmTruncationNote(items.length, body.total);
      if (note) notify.info(note);
    } catch {
      setError('Failed to load requisitions for review.');
      setRows([]);
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
    // `dateFilter` IS a dependency now that the window is a server parameter --
    // without it the chip would change the dropdown and refetch nothing.
  }, [history, dateFilter, notify]);

  useEffect(() => { void load(); }, [load]);

  /** The TOP-BAR title follows the view, as legacy's does: its History action
   *  opens Requisition_ListView_ViewMode, a different ListView that the running
   *  app captions "Requisition Review History". Uses the SHARED
   *  `ai:topbar-title` override AppShell already listens for. */
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('ai:topbar-title',
      { detail: history ? 'Requisition Review History' : null }));
    return () => { window.dispatchEvent(new CustomEvent('ai:topbar-title', { detail: null })); };
  }, [history]);

  const filtered = useMemo(() => {
    // Date filtering happens server-side now (see the load above); this is search only.
    const base = rows;
    const t = search.trim().toLowerCase();
    if (!t) return base;
    return base.filter(r =>
      (r.rqid ?? '').toLowerCase().includes(t) ||
      r.itemName.toLowerCase().includes(t) ||
      (r.vendorName ?? '').toLowerCase().includes(t) ||
      (r.requestedByName ?? '').toLowerCase().includes(t)
    );
  }, [rows, search]);

  // Columns mirror the LIVE ALPACA GoLive Requisition Review grid
  // (Requisition_ListView_Review), in that order with the legacy captions:
  // Date . Item . Description . Catalog# . Vendor . OrderQty .
  // DeliveryPriority . Department . RequestedBy . DateReviewed . ReviewedBy.
  // NOTE the shipped Model.xafml also marks Division/Manufacturer/Project/
  // Comment visible and hides Item; the running 26.9.4 grid does the opposite
  // (its layout lives in dbo.ModelDifference). The live grid wins -- same
  // precedent as Item Requisition.
  const cols: AppColumn<RequisitionRow>[] = [
    { field: 'requestedDate',        header: 'Date',             width: 100, text: r => fmtLocalDate(r.requestedDate) ?? '', render: r => fmtLocalDate(r.requestedDate) ?? '' },
    { field: 'itemName',             header: 'Item',             width: 170, text: r => r.itemName },
    { field: 'description',          header: 'Description',      width: 200, text: r => r.description ?? '', render: r => r.description ?? '' },
    { field: 'catalog',              header: 'Catalog#',         width: 130, text: r => r.catalog ?? '', render: r => r.catalog ?? '' },
    { field: 'vendorName',           header: 'Vendor',           width: 150, text: r => r.vendorName ?? '', render: r => r.vendorName ?? '' },
    /* Manufacturer and PackUnits are on the HISTORY view only
       (Requisition_ListView_ViewMode Index 5 and 7); the review QUEUE
       (Requisition_ListView_Review) does not show them. */
    ...(history ? [
      { field: 'manufacturerName', header: 'Manufacturer', width: 150,
        text: (r: RequisitionRow) => r.manufacturerName ?? '',
        render: (r: RequisitionRow) => r.manufacturerName ?? '' },
    ] as AppColumn<RequisitionRow>[] : []),
    { field: 'orderQty',             header: 'OrderQty',         width: 90, type: 'number', text: r => String(r.orderQty), value: r => r.orderQty, edit: !perm.write ? undefined : { type: 'number' as const, requiredEmpty: (r: RequisitionRow) => !r.orderQty } },
    ...(history ? [
      { field: 'packUnits', header: 'PackUnits', width: 95,
        text: (r: RequisitionRow) => r.packUnits ?? '',
        render: (r: RequisitionRow) => r.packUnits ?? '' },
      { field: 'stockQty', header: 'StockQty', width: 90, type: 'number',
        text: (r: RequisitionRow) => String(r.stockQty), value: (r: RequisitionRow) => r.stockQty },
    ] as AppColumn<RequisitionRow>[] : []),
    { field: 'deliveryPriorityName', header: 'DeliveryPriority', width: 130, text: r => r.deliveryPriorityName ?? '', render: r => r.deliveryPriorityName ?? '', edit: !perm.write ? undefined : { type: 'combo' as const, options: priorities.map(o => o.name), requiredEmpty: (r: RequisitionRow) => !r.deliveryPriorityName } },
    { field: 'departmentName',       header: 'Department',       width: 120, text: r => r.departmentName ?? '', render: r => r.departmentName ?? '', edit: !perm.write ? undefined : { type: 'combo' as const, options: departments.map(o => o.name), requiredEmpty: (r: RequisitionRow) => !r.departmentName } },
    { field: 'requestedByName',      header: 'RequestedBy',      width: 130, text: r => r.requestedByName ?? '', render: r => r.requestedByName ?? '' },
    /* Date AND time, as G5's DateReviewed shows them (10/26/2026 05:02). The
       Date column beside it stays date-only: G5 shows that one without a time. */
    { field: 'reviewedDate',         header: 'DateReviewed',     width: 150,
      text:   r => fmtLocalDateTime(reviewStamps.get(r.oid) ?? r.reviewedDate) ?? '',
      render: r => fmtLocalDateTime(reviewStamps.get(r.oid) ?? r.reviewedDate) ?? '' },
    { field: 'reviewedByName',       header: 'ReviewedBy',       width: 130,
      text:   r => (reviewStamps.has(r.oid) ? meName : r.reviewedByName) ?? '',
      render: r => (reviewStamps.has(r.oid) ? meName : r.reviewedByName) ?? '' },
  ];

  /** A rule the server rejected ("Requisition is not in a reviewable state.",
   *  "Vendor must not be empty", ...) arrives as a 400 BUSINESS_RULE carrying
   *  the message. Swallowing it left the user with a bare "Review failed."
   *  and nothing to act on — exactly how a broken BatchID applock hid behind
   *  that wording until 2026-09-29. */
  const ruleMessage = (e: unknown) =>
    (e as { response?: { data?: { error?: { message?: string } } } })
      ?.response?.data?.error?.message;

  /** The ONE legacy action on this view (Review_Execute). It stamps
   *  ReviewedDate/ReviewedBy and moves the row to PendingOrdering. The button
   *  used to post action='approve', which stamped ApprovedDate instead and —
   *  after the status remap — was rejected outright by the SP's reviewable
   *  guard, surfacing as "Approve failed." (user report 2026-09-24). */
  async function handleAction(action: 'review') {
    if (selected.size === 0) { notify.warning('Select at least one requisition.'); return; }
    const ids = Array.from(selected) as string[];
    const label = 'Review';
    if (!(await confirm({
      title: `${label} Requisition(s)?`,
      message: `${label} ${ids.length} requisition(s)?`,
      confirmLabel: label,
    }))) return;
    setActing(true);
    try {
      await Promise.all(ids.map(id =>
        api.post(`/api/v1/requisitions/${id}/review`, {}, { params: { action } })));
      setSelected(new Set()); setReviewStamps(new Map());
      await load();
      notify.success(`Review completed successfully.`);
    } catch (e) {
      notify.error(ruleMessage(e) ?? `${label} failed.`);
    } finally {
      setActing(false);
    }
  }

  /** Legacy ICMRollBackAction — captioned "Cancel" at runtime. Prompts for a
   *  reason, then cancels each selected requisition. */
  async function cancelSelected() {
    if (selected.size === 0) { notify.warning('Please select an item to proceed further.'); return; }
    const reason = await prompt({
      title: 'Reason',
      message: 'Enter the reason for cancelling the selected requisition(s).',
      required: true,                                  // legacy blocks an empty reason
      confirmLabel: 'Cancel Requisition',
    });
    if (reason === null) return;                       // dismissed
    if (!reason.trim()) { notify.warning('Please enter the cancel reason.'); return; }
    const ids = Array.from(selected) as string[];
    setActing(true);
    try {
      await Promise.all(ids.map(id =>
        api.post(`/api/v1/requisitions/${id}/cancel`, { reason })));
      setSelected(new Set()); setReviewStamps(new Map());
      await load();
      notify.success('Cancelled successfully.');
    } catch (e) {
      notify.error(ruleMessage(e) ?? 'Cancel failed.');
    } finally {
      setActing(false);
    }
  }

  /** ROLLBACK — legacy ICMRollbackViewController's Requisition_ListView_ViewMode
   *  branch (~746): send an already-reviewed requisition BACK to the review
   *  queue. It clears ReviewedBy / ReviewedDate, stores the reason and sets the
   *  status to Pending Review.
   *
   *  Legacy asks for the reason in an "ICM RollBack" popup first and refuses to
   *  commit without one (DialogController_Accepting). It also silently SKIPS a
   *  row that is not in a rollback-able state — already Pending Review, or
   *  already Partially Received / Received / Cancelled — which is how a user
   *  ends up believing a rollback worked. This checks the selection up front and
   *  says so; the SP re-checks and refuses too. */
  async function rollbackSelected() {
    if (selected.size === 0) { notify.warning('Please select an item to proceed further.'); return; }

    // Legacy's eligible set: PendingApproval, PendingOrdering,
    // PendingOrderingApproval, PendingReceived and the ten Level-N Pending
    // statuses — i.e. reviewed, but not yet received.
    const ROLLBACKABLE = new Set([
      'Pending Approval', 'Pending Ordering', 'Pending Ordering Approval', 'Pending Received',
      ...Array.from({ length: 10 }, (_, i) => `Level ${i + 1} Pending`),
    ]);
    const blocked = rows.filter(r => selected.has(r.oid) && !ROLLBACKABLE.has(r.statusLabel));
    if (blocked.length > 0) {
      notify.error('Only a reviewed requisition that has not yet been received can be rolled back.');
      return;
    }

    const reason = await prompt({
      title: 'ICM RollBack',
      message: 'Enter the reason for rolling back the selected requisition(s).',
      required: true,
      confirmLabel: 'RollBack',
    });
    if (reason === null) return;
    if (!reason.trim()) { notify.warning('Please enter the rollback reason.'); return; }

    const ids = Array.from(selected) as string[];
    setActing(true);
    try {
      for (const id of ids) {
        await api.post(`/api/v1/requisitions/${id}/rollback`, { reason });
      }
      setSelected(new Set()); setReviewStamps(new Map());
      await load();
      notify.success('Rollback completed successfully.');
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message;
      notify.error(msg ?? 'Rollback failed.');
    } finally {
      setActing(false);
    }
  }

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>ICM Operations</span><i className="ti ti-chevron-right" />
            {history ? (
              <>
                {/* The way BACK, now that History drops the "Pending" button —
                    legacy returns through its own navigation. */}
                <span onClick={() => { setSelected(new Set()); setReviewStamps(new Map()); setHistory(false); }}
                      style={{ cursor: 'pointer' }}>Requisition Review</span>
                <i className="ti ti-chevron-right" />
                <span className="dp-bc-active">Requisition Review History</span>
              </>
            ) : (
              <span className="dp-bc-active">Requisition Review</span>
            )}
          </div>
          <div className="dp-title-row">
            <FormTitle
              pageId="icm-requisition-review" auditFormName="Requisition Review"
              title={history ? 'Requisition Review History' : 'Requisition Review'}
              loadMs={loadMs}
              loading={loading}
            />
          </div>
        </div>
      </div>

      <div className="bl-grid-area">
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            left={
              <>
                {/* QUEUE-ONLY ACTIONS. Legacy's History opens
                    Requisition_ListView_ViewMode, whose bar is RollBack · Date
                    Filter · Export to · Search — Review, Cancel and the History
                    toggle belong to the review QUEUE
                    (Requisition_ListView_Review) and are not on it. */}
                {!history && (
                  <>
                    {perm.write && <button className="ca-btn" disabled={selected.size === 0 || acting} onClick={() => handleAction('review')}>
                      <i className="ti ti-circle-check" /> Review
                    </button>}
                    {perm.write && <button className="ca-btn" style={{ marginLeft: 6 }} disabled={selected.size === 0 || acting} onClick={cancelSelected}>
                      <i className="ti ti-rotate" /> Cancel
                    </button>}
                    <button className="ca-btn" style={{ marginLeft: 6 }}
                      onClick={() => { setSelected(new Set()); setReviewStamps(new Map()); setHistory(true); }}
                      title="Show reviewed requisitions">
                      <i className="ti ti-history" /> History
                    </button>
                  </>
                )}
                {/* ROLLBACK — the ONE action legacy puts on the History view
                    (ICMRollBackAction, captioned "RollBack" there). */}
                {history && perm.write && (
                  <button className="ca-btn" disabled={selected.size === 0 || acting} onClick={rollbackSelected}>
                    <i className="ti ti-arrow-back-up" /> RollBack
                  </button>
                )}
                <DateFilterSelect value={dateFilter} onChange={setDateFilter} />
              </>
            }
            onRefresh={load}
            refreshing={loading}
            exportData={{ filename: 'requisition-review', ...gridExportMatrix(filtered, cols) }}
            /* Legacy puts Search INSIDE the action bar, after "Export to"
               (Requisition_ListView_Review), not on a row of its own. */
            right={
              <span className="list-search" style={{ marginLeft: 10 }}>
                <span className="tb-i">🔍</span>
                <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Text to search..." />
                {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
              </span>
            }
          />
        </div>
        <div className="bl-card bl-card-flex">
          {error ? (
            <div className="bl-err"><i className="ti ti-alert-circle" /> {error}</div>
          ) : (
            <AppDataGrid<RequisitionRow>
              rows={filtered}
              columns={cols}
              getRowId={r => r.oid}
              rowEdit={{ onSave: saveRow }}
              loading={loading}
              loadMs={loadMs}
              selectable
              selectedIds={selected}
              onSelectionChange={handleSelectionChange}
              emptyText="No requisitions pending review or approval."
            />
          )}
        </div>
      </div>
    </div>
  );
}
